import {
  useComments,
  useAddComment,
  useEditComment,
  useDeleteComment,
  mapCommentDtoToEnhancedComment,
  CommentsSortOrder,
  CommentsSortBy,
  CommentsCategory
} from '@domain/comments'
import {DataContentWrapper} from '@hconnect/common/containers'
import {headlineSx} from '@hconnect/common/styles'
import {getSystemTimezone} from '@hconnect/common/utils'
import {useUserContext} from '@hconnect/login'
import {CardSectionTitle} from '@hconnect/uikit/src/lib2'
import {ControlledCommentBox, ControlledCommentProps} from '@hm/fundamental'
import {Box, Divider} from '@mui/material'
import {useState} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../consts'

type Props = {
  plantId: string
  commentCategoryInstanceId: string
  commentsCategory: CommentsCategory
  queryKeyPrefix: string
}

export const CommentsContainer = ({
  plantId,
  commentCategoryInstanceId,
  commentsCategory,
  queryKeyPrefix
}: Props) => {
  const {t, i18n} = useTranslation(FEATURE_NAMESPACE)
  const language = i18n.language
  const timezone = getSystemTimezone()
  const {loggedUser} = useUserContext()
  const currentUserName = loggedUser?.name
  const [commentInputValue, setCommentInputValue] = useState('')
  const commentsSortOrder = CommentsSortOrder.Descending
  const commentsSortBy = CommentsSortBy.CreatedOn
  const {
    data: comments,
    isInitialLoading,
    isError,
    isRefetching,
    refetch
  } = useComments(
    {
      plantId,
      commentCategoryInstanceIds: [commentCategoryInstanceId],
      commentsCategory,
      commentsSortOrder,
      commentsSortBy
    },
    queryKeyPrefix
  )
  const {mutateAsync: editComment} = useEditComment(
    {
      plantId,
      commentsCategory
    },
    queryKeyPrefix
  )
  const {mutateAsync: addComment} = useAddComment(
    {
      plantId,
      commentsCategory
    },
    queryKeyPrefix
  )
  const {mutateAsync: deleteComment} = useDeleteComment(
    {
      plantId,
      commentsCategory
    },
    queryKeyPrefix
  )

  const handleDeleteComment = async (id: string) => {
    await deleteComment({id, commentCategoryInstanceId})
  }

  const handleAddComment = async (comment: string) => {
    await addComment({value: comment, commentCategoryInstanceId})
    setCommentInputValue('')
  }

  const handleEditComment = async ({id}: ControlledCommentProps<string>, value: string) => {
    await editComment({value, id, commentCategoryInstanceId})
  }
  const enhancedComments =
    comments?.map((comment) =>
      mapCommentDtoToEnhancedComment(comment, timezone, language, currentUserName)
    ) ?? []
  return (
    <DataContentWrapper
      showLoader={isInitialLoading}
      isError={isError}
      isNoData={false}
      isRefetching={isRefetching}
      retryFunction={refetch}
      progressSx={{color: 'primary.main'}}
    >
      <Box>
        <CardSectionTitle sx={headlineSx()}>{t('task.label.comments')}</CardSectionTitle>
        <Divider sx={{borderColor: 'divider', mb: 1}} />
        <div className="relative">
          <ControlledCommentBox
            comments={enhancedComments}
            commentInputValue={commentInputValue}
            onCommentInputChange={setCommentInputValue}
            onDelete={handleDeleteComment}
            onEdit={handleEditComment}
            onSubmit={handleAddComment}
          />
        </div>
      </Box>
    </DataContentWrapper>
  )
}
